import { Form } from 'antd';
import { useGetEmployeeQuery, useGetImplementQuery } from 'app/api';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { IRouteWithSelectedImplement } from 'app/containers/Task/slice/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  EMPLOYEE_FORM_NAME_IDENTIFIER,
  getTaskCategoryIdsFromSelectedImplements,
  IMPLEMENT_FORM_NAME_IDENTIFIER,
  IMPLEMENT_FORM_NAME_SEPARATOR,
  mapGroupedRoutesWithSelectedImplement,
} from '../AddRouteToTaskDrawer.helpers';
import { useAddRoutesToTask } from './useAddRoutesToTask.';
import { useGetRoutesTableItems } from './useGetRoutesTableItems';

export const useAddRouteToTaskDrawer = ({ isOpen }: { isOpen: boolean }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const envId = useSelector(selectSelectedEnvId) as number;
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const companyId = useSelector(selectSelectedCompanyId);

  const [rowsWithSelectedImplement, setRowsWithSelectedImplements] = useState<
    IRouteWithSelectedImplement[]
  >([]);

  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTaskId(null);
      setRowsWithSelectedImplements([]);
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: implementResponse } = useGetImplementQuery({
    companyId,
    companyGroupId,
    envId,
    pageSize: 1000,
    currentPage: 0,
    filters: {},
    sort: {},
  });

  const { data: employeeResponse } = useGetEmployeeQuery({
    companyId,
    companyGroupId,
    envId,
    pageSize: 1000,
    currentPage: 0,
    filters: {},
    sort: {},
  });

  const {
    groupedRoutes,
    routeTableItems,
    routesWithInitiallySelectedImplement,
    maxToDate,
    minFromDate,
  } = useGetRoutesTableItems();

  useEffect(() => {
    if (isOpen && routesWithInitiallySelectedImplement) {
      setRowsWithSelectedImplements(
        routesWithInitiallySelectedImplement.map(
          ({ routeId, implementId }) => ({ routeId: routeId, implementId }),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onValuesChange = (
    changedValues: { [key: string]: number },
    values: { [key: string]: number },
  ) => {
    const entries = Object.entries(values);
    const implementEntries = entries.filter(([key]) =>
      key.includes(IMPLEMENT_FORM_NAME_IDENTIFIER),
    );

    const employeeValues = entries
      .filter(([key]) => key.includes(EMPLOYEE_FORM_NAME_IDENTIFIER))
      .map(([key, value]) => {
        const routeId = Number(key.split(IMPLEMENT_FORM_NAME_SEPARATOR)[0]);
        return { routeId, implementId: value };
      });

    const implementRows: IRouteWithSelectedImplement[] = implementEntries.map(
      ([key, implementId]) => {
        const routeId = Number(key.split(IMPLEMENT_FORM_NAME_SEPARATOR)[0]);

        const employeeId = employeeValues.find(
          employeeVal => employeeVal.routeId === routeId,
        )?.implementId;

        return { routeId, implementId, employeeId };
      },
    );

    setRowsWithSelectedImplements(implementRows);
  };

  const implementIds = rowsWithSelectedImplement
    .map(rowData => rowData.implementId)
    .filter(id => id);

  const selectedImplements = implementResponse?.content.filter(implement =>
    implementIds.includes(implement.id),
  );

  const taskCategoryIds = getTaskCategoryIdsFromSelectedImplements(
    selectedImplements,
  );

  // const { startDate, endDate } = getRoutesStartAndEndDates(routes);

  const isAllRouteGroupsHaveSelectedImplement =
    Boolean(rowsWithSelectedImplement.length) &&
    !rowsWithSelectedImplement?.find(row => !row.implementId);

  const isInitialDataLoaded = Boolean(implementResponse);

  const initialValues = routesWithInitiallySelectedImplement.reduce(
    (
      accumulator: { [key: string]: number | undefined },
      { formItemName, implementId },
    ) => {
      accumulator[formItemName] = implementId;
      return accumulator;
    },
    {},
  );

  const groupedRoutesWithSelectedImplement = mapGroupedRoutesWithSelectedImplement(
    {
      groupedRoutes,
      rowsWithSelectedImplement,
      implementArray: implementResponse?.content,
    },
  );

  const { onMoveRoutesToTask, onCreateNewTask } = useAddRoutesToTask({
    employeeArray: employeeResponse?.content,
    implementArray: implementResponse?.content,
    groupedRoutes,
    routeTableItems,
    rowsWithSelectedImplement,
    selectedTaskId,
  });

  return {
    onCreateNewTask,
    selectedTaskId,
    t,
    isInitialDataLoaded,
    form,
    initialValues,
    onValuesChange,
    groupedRoutesWithSelectedImplement,
    maxToDate,
    minFromDate,
    taskCategoryIds,
    isAllRouteGroupsHaveSelectedImplement,
    setSelectedTaskId,
    onMoveRoutesToTask,
    routeTableItems,
  };
};
